import { useCallback, useEffect, useRef, useState } from 'react';
import type { AvatarTemplate, ImageTransform } from '../../types/avatar';

interface AvatarCanvasProps {
  template: AvatarTemplate;
  userImage: HTMLImageElement | null;
  transform: ImageTransform;
  logoMode?: 'text' | 'logo';
  onTransformChange: (transform: ImageTransform | ((prev: ImageTransform) => ImageTransform)) => void;
  onCanvasReady?: (canvas: HTMLCanvasElement) => void;
}

export function AvatarCanvas({
  template,
  userImage,
  transform,
  logoMode = 'text',
  onTransformChange,
  onCanvasReady,
}: AvatarCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  // Redraw canvas whenever template, userImage, transform, or logoMode changes
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = template;
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    ctx.clearRect(0, 0, width, height);

    // 1. Draw User Image (if present) or neutral placeholder background
    if (userImage && userImage.width > 0 && userImage.height > 0) {
      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Fit image cover scale initially
      const imgRatio = userImage.width / userImage.height;
      const canvasRatio = width / height;
      let baseW: number;
      let baseH: number;

      if (imgRatio > canvasRatio) {
        baseH = height;
        baseW = height * imgRatio;
      } else {
        baseW = width;
        baseH = width / imgRatio;
      }

      // Center point
      const centerX = width / 2;
      const centerY = height / 2;

      ctx.translate(centerX + transform.panX, centerY + transform.panY);
      ctx.rotate((transform.rotation * Math.PI) / 180);
      ctx.scale(transform.zoom, transform.zoom);

      ctx.drawImage(
        userImage,
        -baseW / 2,
        -baseH / 2,
        baseW,
        baseH
      );

      ctx.restore();
    } else {
      // Clean placeholder background with subtle soft gradient
      const bgGrad = ctx.createLinearGradient(0, 0, width, height);
      bgGrad.addColorStop(0, '#f8fafc');
      bgGrad.addColorStop(1, '#e8eef7');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, width, height);

      // Subtle cue
      ctx.fillStyle = '#7a8fae';
      ctx.font = '700 36px "Clash Display", "Switzer", Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Envie ou tire uma foto para começar', width / 2, height / 2);
    }

    // 2. Draw Template Overlay (Borders, Badges, Texts, Logo)
    template.renderOverlay(ctx, width, height, {
      logoMode,
    });
  }, [template, userImage, transform, logoMode]);

  useEffect(() => {
    render();
  }, [render]);

  useEffect(() => {
    if (canvasRef.current && onCanvasReady) {
      onCanvasReady(canvasRef.current);
    }
  }, [onCanvasReady]);

  // Pointer interactions for direct dragging/panning
  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!userImage) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    isDraggingRef.current = true;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDraggingRef.current || !canvasRef.current || !userImage) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const scale = template.width / rect.width;

    const dx = (e.clientX - dragStartRef.current.x) * scale;
    const dy = (e.clientY - dragStartRef.current.y) * scale;

    dragStartRef.current = { x: e.clientX, y: e.clientY };

    onTransformChange((prev) => ({
      ...prev,
      panX: prev.panX + dx,
      panY: prev.panY + dy,
    }));
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (isDraggingRef.current) {
      try {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        // Ignore if pointer capture release is unsupported
      }
      isDraggingRef.current = false;
    }
  };

  const isAvatar = template.category === 'avatar';

  return (
    <div
      className="avatar-canvas-wrapper relative mx-auto overflow-hidden rounded-2xl bg-slate-900 ring-2 ring-[#06194d]/15 select-none shadow-lg flex items-center justify-center"
      style={{
        maxHeight: isAvatar ? 'min(32vh, 260px)' : 'min(32vh, 275px)',
        aspectRatio: isAvatar ? '1 / 1' : '4 / 5',
        width: 'auto',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <canvas
        ref={canvasRef}
        width={template.width}
        height={template.height}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        className={`h-full w-full object-contain ${
          userImage ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
        } transition-transform duration-75`}
        style={{ touchAction: 'none' }}
        aria-label={`Visualização do ${template.name}`}
      />

      {userImage && isHovered && (
        <div className="pointer-events-none absolute inset-x-0 bottom-2.5 flex justify-center text-xs font-semibold text-white/95 drop-shadow">
          <span className="rounded-full bg-black/65 px-3 py-0.5 backdrop-blur-xs text-[11px] tracking-wide">
            Arraste para posicionar
          </span>
        </div>
      )}
    </div>
  );
}
